import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { Plus, PencilLine, Trash, Camera, Warning, X } from "@phosphor-icons/react";
import api, { apiError } from "../lib/api";
import { zar } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const TYPE_LABELS = { supplier_invoice: "Supplier Invoice", receipt: "Receipt", credit_note: "Credit Note" };
const TYPE_STYLES = {
  supplier_invoice: "bg-[#A78BFA]/10 text-[#A78BFA]",
  receipt: "bg-[#A9C97E]/10 text-[#A9C97E]",
  credit_note: "bg-[#F87171]/10 text-[#F87171]",
};
const DEPT_STYLES = { Food: "text-[#A9C97E]", Beverage: "text-[#2B6CB0]", Other: "text-[#595959]" };

const EMPTY_FORM = { date: format(new Date(), "yyyy-MM-dd"), type: "supplier_invoice", supplier_id: "", category_id: "", amount: "" };

// Client-side capture guidance: brightness + edge-sharpness heuristics on a downscaled canvas.
const analyzeImage = (file) => new Promise((resolve) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    const w = 160, h = Math.max(1, Math.round((160 * img.height) / img.width));
    const c = document.createElement("canvas");
    c.width = w; c.height = h;
    const ctx = c.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    const d = ctx.getImageData(0, 0, w, h).data;
    const gray = new Float32Array(w * h);
    let sum = 0;
    for (let i = 0; i < w * h; i++) {
      gray[i] = 0.299 * d[4 * i] + 0.587 * d[4 * i + 1] + 0.114 * d[4 * i + 2];
      sum += gray[i];
    }
    let edge = 0;
    for (let y = 1; y < h - 1; y++) for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      edge += Math.abs(4 * gray[i] - gray[i - 1] - gray[i + 1] - gray[i - w] - gray[i + w]);
    }
    URL.revokeObjectURL(url);
    resolve({ brightness: sum / (w * h), sharpness: edge / (w * h), minDim: Math.min(img.width, img.height) });
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
  img.src = url;
});

export default function Invoices({ embedded = false }) {
  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [range, setRange] = useState([format(startOfMonth(new Date()), "yyyy-MM-dd"), format(endOfMonth(new Date()), "yyyy-MM-dd")]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [ocrOpen, setOcrOpen] = useState(false);
  const [ocrFiles, setOcrFiles] = useState([]);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const [ocrMode, setOcrMode] = useState("single");
  const navigate = useNavigate();

  const pickOcrFiles = async (fileList) => {
    const files = Array.from(fileList || []).slice(0, 10);
    const analyzed = await Promise.all(files.map(async (file) => {
      const warnings = [];
      if (file.type === "application/pdf") {
        warnings.push(null);
      } else {
        const a = await analyzeImage(file);
        if (!a) warnings.push("Couldn't read this file");
        else {
          if (a.brightness < 50) warnings.push("Too dark — retake in better light");
          if (a.sharpness < 3) warnings.push("Looks blurry — hold steady and retake");
          if (a.minDim < 500) warnings.push("Low resolution — move closer");
        }
      }
      return { file, warnings: warnings.filter(Boolean) };
    }));
    setOcrFiles((prev) => [...prev, ...analyzed].slice(0, 10));
    setOcrMode(ocrFiles.length + analyzed.length > 1 ? "batch" : "single");
    setOcrResult(null);
  };

  const submitOcr = async () => {
    if (ocrFiles.length === 0) return toast.error("Add at least one photo or PDF");
    setOcrBusy(true);
    try {
      const fd = new FormData();
      ocrFiles.forEach((f) => fd.append("files", f.file));
      fd.append("mode", ocrMode);
      const { data } = await api.post("/invoices/ocr", fd);
      if (ocrMode === "batch" && data.job_ids.length > 1) {
        const tracker = ocrFiles.map((f, i) => ({ name: f.file.name, jobId: data.job_ids[i], status: "processing" }));
        setOcrResult({ status: "batch", items: [...tracker] });
        const poll = async () => {
          let pending = false;
          for (const t of tracker) {
            if (t.status === "processing") {
              try { Object.assign(t, (await api.get(`/invoices/ocr/${t.jobId}`)).data); } catch (err) { console.warn("OCR poll failed, will retry:", err?.message); }
            }
            if (t.status === "processing") pending = true;
          }
          setOcrResult({ status: "batch", items: [...tracker] });
          return pending;
        };
        for (let i = 0; i < 40; i++) {
          await new Promise((r) => setTimeout(r, 2500));
          if (!(await poll())) break;
        }
        tracker.forEach((t) => { if (t.status === "processing") { t.status = "failed"; t.error = "Timed out"; } });
        setOcrResult({ status: "batch", items: [...tracker] });
      } else {
        setOcrResult({ status: "processing" });
        for (let i = 0; i < 40; i++) {
          await new Promise((r) => setTimeout(r, 2500));
          const jr = await api.get(`/invoices/ocr/${data.job_id}`);
          const st = jr.data.status;
          if (st === "done" || st === "duplicate" || st === "failed") { setOcrResult(jr.data); break; }
          if (i === 39) setOcrResult({ status: "failed", error: "Extraction timed out — try again or use manual entry" });
        }
      }
      load();
    } catch (e) {
      setOcrResult({ status: "failed", error: apiError(e, "Upload failed") });
    } finally {
      setOcrBusy(false);
    }
  };

  const resetOcr = () => { setOcrFiles([]); setOcrResult(null); setOcrMode("single"); };

  const load = () => {
    api.get(`/invoices?start=${range[0]}&end=${range[1]}`).then((r) => setInvoices(r.data)).catch(() => toast.error("Failed to load invoices"));
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    api.get("/invoice-categories").then((r) => setCategories(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [range]);

  const openAdd = () => { setEditing(null); setForm(EMPTY_FORM); setOpen(true); };
  const openEdit = (inv) => {
    setEditing(inv);
    setForm({ date: inv.date, type: inv.type, supplier_id: inv.supplier_id, category_id: inv.category_id, amount: Math.abs(inv.amount) });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.category_id || !form.amount) return toast.error("Category and amount required");
    if (!form.supplier_id) return toast.error("Pick a supplier");
    setBusy(true);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (editing) {
        await api.put(`/invoices/${editing.id}`, payload);
        toast.success("Invoice updated");
      } else {
        await api.post("/invoices", payload);
        toast.success(form.type === "credit_note" ? "Credit note saved (stored as negative)" : "Invoice saved");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (inv) => {
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(apiError(e, "Delete failed"));
    }
  };

  const posted = invoices.filter((i) => i.status !== "pending_review");
  const net = posted.reduce((a, i) => a + i.amount, 0);
  const byDept = posted.reduce((a, i) => ({ ...a, [i.department]: (a[i.department] || 0) + i.amount }), {});

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        {!embedded && (
          <div>
            <h1 className="font-heading text-3xl font-bold">Invoices</h1>
            <p className="text-muted-foreground mt-1">Supplier invoices, on-the-day receipts and credit notes</p>
          </div>
        )}
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" data-testid="inv-start" value={range[0]} onChange={(e) => setRange([e.target.value, range[1]])} /></div>
          <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" data-testid="inv-end" value={range[1]} onChange={(e) => setRange([range[0], e.target.value])} /></div>
          <Button variant="outline" data-testid="ocr-upload-btn" onClick={() => { resetOcr(); setOcrOpen(true); }}>
            <Camera size={16} className="mr-1" /> Upload photo / PDF
          </Button>
          <Button data-testid="add-invoice-btn" onClick={openAdd} className="bg-primary hover:bg-[#8B5CF6] text-white">
            <Plus size={16} className="mr-1" /> Add invoice
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          ["Net spend (period)", zar(net), "inv-total"],
          ["Food", zar(byDept.Food || 0), "inv-food"],
          ["Beverage", zar(byDept.Beverage || 0), "inv-bev"],
          ["Other", zar(byDept.Other || 0), "inv-other"],
        ].map(([label, val, tid]) => (
          <Card key={tid} className="border-border shadow-none" data-testid={tid}>
            <CardContent className="pt-5">
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className={`font-heading text-2xl font-bold mt-1 ${val.startsWith("-") ? "text-[#F87171]" : ""}`}>{val}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="glass border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]" data-testid="invoices-table">
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3 font-medium">Due date</th>
              <th className="px-4 py-3 font-medium">Type</th>
              <th className="px-4 py-3 font-medium">Supplier</th>
              <th className="px-4 py-3 font-medium">Category</th>
              <th className="px-4 py-3 font-medium">Dept</th>
              <th className="px-4 py-3 font-medium text-right">Amount</th>
              <th className="px-4 py-3 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id} className="border-b last:border-0" data-testid={`invoice-row-${inv.id}`}>
                <td className="px-4 py-2.5">{inv.date}</td>
                <td className="px-4 py-2.5" data-testid={`invoice-due-${inv.id}`}>{inv.due_date || "—"}</td>
                <td className="px-4 py-2.5">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_STYLES[inv.type]}`}>{TYPE_LABELS[inv.type]}</span>
                  {inv.status === "pending_review" && <span className="ml-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-[#F87171]/10 text-[#F87171]" data-testid={`invoice-pending-${inv.id}`}>Review</span>}
                </td>
                <td className="px-4 py-2.5 font-medium">{inv.supplier_name}</td>
                <td className="px-4 py-2.5">{inv.category_name}</td>
                <td className={`px-4 py-2.5 text-xs font-medium ${DEPT_STYLES[inv.department]}`}>{inv.department}</td>
                <td className={`px-4 py-2.5 num font-medium ${inv.amount < 0 ? "text-[#F87171]" : ""}`}>{zar(inv.amount)}</td>
                <td className="px-4 py-2.5">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" data-testid={`invoice-edit-${inv.id}`} onClick={() => openEdit(inv)}><PencilLine size={15} /></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" data-testid={`invoice-delete-${inv.id}`} className="text-destructive hover:text-destructive"><Trash size={15} /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
                          <AlertDialogDescription>{TYPE_LABELS[inv.type]} · {inv.supplier_name} · {zar(inv.amount)} on {inv.date}. Recorded in the audit log.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(inv)} data-testid={`invoice-delete-confirm-${inv.id}`} className="bg-destructive text-white hover:bg-destructive/90">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </td>
              </tr>
            ))}
            {invoices.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No invoices in this range.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="invoice-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit entry" : "Add invoice / receipt"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="invoice-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Supplier</Label>
              <Select value={form.supplier_id} onValueChange={(v) => {
                const sup = suppliers.find((s) => s.id === v);
                setForm({ ...form, supplier_id: v, category_id: sup?.default_category_id || form.category_id });
              }}>
                <SelectTrigger data-testid="invoice-supplier"><SelectValue placeholder="Pick supplier…" /></SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  {suppliers.length === 0 && <p className="px-2 py-2 text-xs text-muted-foreground">No suppliers yet — add them in the Suppliers directory.</p>}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger data-testid="invoice-category"><SelectValue placeholder="Pick category…" /></SelectTrigger>
                <SelectContent>
                  {["Food", "Beverage", "Other"].map((dept) => (
                    categories.filter((c) => c.department === dept).length > 0 && (
                      <div key={dept}>
                        <p className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase">{dept}</p>
                        {categories.filter((c) => c.department === dept).map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </div>
                    )
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Amount (R)</Label>
                <Input type="number" step="any" min="0" data-testid="invoice-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                {form.type === "credit_note" && <p className="text-[10px] text-muted-foreground">Stored as negative — nets off spend automatically.</p>}
              </div>
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input type="date" data-testid="invoice-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
            </div>
            <Button onClick={submit} disabled={busy} data-testid="invoice-save-btn" className="w-full bg-primary hover:bg-[#8B5CF6] text-white">
              {busy ? "Saving…" : editing ? "Save changes" : "Save entry"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={ocrOpen} onOpenChange={(o) => { if (!ocrBusy) { setOcrOpen(o); if (!o) resetOcr(); } }}>
        <DialogContent className="max-w-md" data-testid="ocr-dialog">
          <DialogHeader><DialogTitle className="font-heading">Upload invoice photo / PDF</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {!ocrResult && (
              <>
                <label htmlFor="ocr-file-input" className="block border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors">
                  <Camera size={28} className="mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">JPG, PNG or PDF (max 10 files) — snap several invoices at once, or multiple pages of one</p>
                  <input id="ocr-file-input" data-testid="ocr-file-input" type="file" multiple accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden"
                    onChange={(e) => { pickOcrFiles(e.target.files); e.target.value = ""; }} />
                </label>
                {ocrFiles.length > 0 && (
                  <div className="space-y-2" data-testid="ocr-file-list">
                    {ocrFiles.map((f, i) => (
                      <div key={`${f.file.name}-${f.file.size}-${i}`} className="flex items-center justify-between gap-2 text-sm border border-border rounded-md px-3 py-2">
                        <span className="truncate">{f.file.name}</span>
                        <span className="flex items-center gap-2">
                          {f.warnings.map((w) => <span key={w} className="text-[#F87171] text-xs flex items-center" data-testid={`ocr-warning-${i}`}><Warning size={12} className="mr-0.5" />{w}</span>)}
                          <button onClick={() => setOcrFiles(ocrFiles.filter((_, j) => j !== i))} data-testid={`ocr-remove-${i}`} className="text-muted-foreground hover:text-destructive"><X size={14} /></button>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {ocrFiles.length > 1 && (
                  <div className="flex gap-2" data-testid="ocr-mode-toggle">
                    <button onClick={() => setOcrMode("batch")} data-testid="ocr-mode-batch"
                      className={`flex-1 text-xs px-3 py-2 rounded-md border transition-colors ${ocrMode === "batch" ? "border-primary bg-primary/10 text-primary font-medium" : "border-border text-muted-foreground"}`}>
                      Separate invoices
                    </button>
                    <button onClick={() => setOcrMode("single")} data-testid="ocr-mode-single"
                      className={`flex-1 text-xs px-3 py-2 rounded-md border transition-colors ${ocrMode === "single" ? "border-primary bg-primary/10 text-primary font-medium" : "border-border text-muted-foreground"}`}>
                      Pages of one invoice
                    </button>
                  </div>
                )}
                <Button onClick={submitOcr} disabled={ocrFiles.length === 0} data-testid="ocr-submit-btn" className="w-full bg-primary hover:bg-[#8B5CF6] text-white">
                  Extract with AI
                </Button>
              </>
            )}
            {ocrResult?.status === "processing" && (
              <div className="py-8 text-center" data-testid="ocr-processing">
                <p className="font-heading text-lg animate-pulse">Extracting invoice data…</p>
                <p className="text-sm text-muted-foreground mt-1">Gemini is reading the document — usually under 30 seconds.</p>
              </div>
            )}
            {ocrResult?.status === "done" && ocrResult.auto_accepted && (
              <div className="py-6 text-center space-y-3" data-testid="ocr-auto-accepted">
                <p className="font-heading text-lg text-[#A9C97E]">Invoice posted automatically</p>
                <p className="text-sm text-muted-foreground">{ocrResult.invoice?.supplier_name} · {zar(ocrResult.invoice?.amount || 0)} · confidence {Math.round((ocrResult.invoice?.extraction_confidence || 0) * 100)}% — all checks passed, inventory updated.</p>
                <Button onClick={() => { setOcrOpen(false); resetOcr(); }} data-testid="ocr-done-close" className="w-full">Done</Button>
              </div>
            )}
            {ocrResult?.status === "done" && !ocrResult.auto_accepted && (
              <div className="py-6 text-center space-y-3" data-testid="ocr-needs-review">
                <p className="font-heading text-lg">Extraction done — needs a quick review</p>
                <p className="text-sm text-muted-foreground">{ocrResult.invoice?.supplier_name} · {zar(ocrResult.invoice?.amount || 0)} · a few fields need confirmation before posting to inventory.</p>
                <Button onClick={() => { setOcrOpen(false); navigate("/stock?tab=review"); }} data-testid="ocr-open-review" className="w-full bg-primary hover:bg-[#8B5CF6] text-white">Open review queue</Button>
              </div>
            )}
            {ocrResult?.status === "duplicate" && (
              <div className="py-6 text-center space-y-3" data-testid="ocr-duplicate">
                <p className="font-heading text-lg text-[#F87171]">Duplicate invoice blocked</p>
                <p className="text-sm text-muted-foreground">This invoice was already captured: {ocrResult.existing_invoice?.supplier_name} · {zar(ocrResult.existing_invoice?.amount || 0)} on {ocrResult.existing_invoice?.date}.</p>
                <Button onClick={() => { setOcrOpen(false); resetOcr(); }} data-testid="ocr-dup-close" className="w-full">Close</Button>
              </div>
            )}
            {ocrResult?.status === "batch" && (
              <div className="space-y-3" data-testid="ocr-batch-result">
                <p className="font-heading text-lg text-center">Batch results</p>
                <div className="space-y-2">
                  {ocrResult.items.map((t, i) => {
                    const label = t.status === "processing" ? "Processing…"
                      : t.status === "done" ? (t.auto_accepted ? "Auto-posted" : "Needs review")
                      : t.status === "duplicate" ? "Duplicate — skipped" : `Failed${t.error ? `: ${t.error}` : ""}`;
                    const cls = t.status === "processing" ? "text-muted-foreground"
                      : t.status === "done" && t.auto_accepted ? "text-[#A9C97E]"
                      : "text-[#F87171]";
                    return (
                      <div key={t.jobId} className="flex items-center justify-between gap-2 text-sm border border-border rounded-md px-3 py-2" data-testid={`ocr-batch-item-${i}`}>
                        <span className="truncate">{t.name}</span>
                        <span className={`text-xs whitespace-nowrap font-medium ${cls}`}>{label}</span>
                      </div>
                    );
                  })}
                </div>
                {ocrResult.items.some((t) => t.status === "done" && !t.auto_accepted) && (
                  <Button onClick={() => { setOcrOpen(false); navigate("/stock?tab=review"); }} data-testid="ocr-batch-open-review" className="w-full bg-primary hover:bg-[#8B5CF6] text-white">
                    Open review queue ({ocrResult.items.filter((t) => t.status === "done" && !t.auto_accepted).length} waiting)
                  </Button>
                )}
                <Button variant="outline" onClick={() => { setOcrOpen(false); resetOcr(); }} data-testid="ocr-batch-done" className="w-full">Done</Button>
              </div>
            )}
            {ocrResult?.status === "failed" && (
              <div className="py-6 text-center space-y-3" data-testid="ocr-failed">
                <p className="font-heading text-lg text-[#F87171]">Extraction failed</p>
                <p className="text-sm text-muted-foreground">{ocrResult.error || "Couldn't read this document."} Manual entry below still works.</p>
                <Button onClick={resetOcr} data-testid="ocr-retry" variant="outline" className="w-full">Try again</Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
